import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../services/api";
import {
  Upload,
  Search,
  Grid,
  List,
  Trash2,
  X,
  FileImage,
  Plus,
} from "lucide-react";
import Button from "../components/Button";
import Input from "../components/Input";
import { getBackendUrl } from "../utils/backendUrl";

export default function MediaLibrary() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [viewMode, setViewMode] = useState("grid");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["media", searchTerm, selectedType],
    queryFn: () =>
      api
        .get("/media", {
          params: {
            search: searchTerm || undefined,
            type: selectedType || undefined,
            per_page: 50,
          },
        })
        .then((res) => res.data),
  });

  const media = data?.data || [];

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/media/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] });
      setSelectedMedia(null);
    },
  });

  const uploadMutation = useMutation({
    mutationFn: (file) => {
      const formData = new FormData();
      formData.append("file", file);
      return api.post("/media/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["media"] });
      setUploading(false);
    },
    onError: () => {
      setUploading(false);
      alert("Failed to upload media");
    },
  });

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploading(true);
      uploadMutation.mutate(file);
    }
  };

  const handleDelete = (media) => {
    if (confirm(`Delete "${media.original_filename}"?`)) {
      deleteMutation.mutate(media.id);
    }
  };

  const copyUrl = (media) => {
    const url = media.url || `/storage/${media.path}`;
    const fullUrl = url.startsWith("http") ? url : `${getBackendUrl()}${url}`;
    navigator.clipboard.writeText(fullUrl);
    alert("URL copied to clipboard");
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1024 / 1024).toFixed(1) + " MB";
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Library</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your uploaded images and videos.
          </p>
        </div>
        <Button onClick={() => fileInputRef.current?.click()}>
          <Plus className="mr-2 h-4 w-4" />
          Add Media
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
      </div>

      <div className="bg-white shadow rounded-lg p-4">
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search media..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="image">Images</option>
            <option value="video">Videos</option>
          </select>
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded ${
                viewMode === "grid"
                  ? "bg-primary-100 text-primary-600"
                  : "text-gray-400 hover:bg-gray-100"
              }`}
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded ${
                viewMode === "list"
                  ? "bg-primary-100 text-primary-600"
                  : "text-gray-400 hover:bg-gray-100"
              }`}
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {uploading && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
          <div className="text-blue-600 font-medium">Uploading media...</div>
        </div>
      )}

      {isLoading ? (
        <div className="text-center py-12">
          <div className="text-gray-500">Loading media...</div>
        </div>
      ) : media.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FileImage className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No media found</p>
          <p className="text-sm text-gray-500">
            {searchTerm || selectedType
              ? "Try adjusting your search or filters"
              : "Upload your first media file"}
          </p>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {media.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-lg shadow hover:shadow-md transition cursor-pointer group"
              onClick={() => setSelectedMedia(item)}
            >
              <div className="aspect-square bg-gray-100 rounded-t-lg overflow-hidden">
                {item.type === "image" ? (
                  <img
                    src={(() => {
                      const url = item.url || `/storage/${item.path}`;
                      return url.startsWith("http")
                        ? url
                        : `${getBackendUrl()}${url}`;
                    })()}
                    alt={item.alt_text || item.original_filename}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src =
                        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Crect fill='%23ddd' width='200' height='200'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='14' dy='10.5'%3EImage%3C/text%3E%3C/svg%3E";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gray-200">
                    <video className="w-full h-full object-cover">
                      <source
                        src={(() => {
                          const url = item.url || `/storage/${item.path}`;
                          return url.startsWith("http")
                            ? url
                            : `${getBackendUrl()}${url}`;
                        })()}
                        type={item.mime_type}
                      />
                    </video>
                  </div>
                )}
              </div>
              <div className="p-2">
                <p className="text-xs text-gray-600 truncate">
                  {item.original_filename}
                </p>
                <div className="flex justify-between items-center mt-1">
                  <span className="text-xs text-gray-400">
                    {formatFileSize(item.size)}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(item);
                    }}
                    className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Preview
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Size
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Uploaded
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {media.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="h-10 w-10 rounded overflow-hidden bg-gray-100">
                      {item.type === "image" ? (
                        <img
                          src={(() => {
                            const url = item.url || `/storage/${item.path}`;
                            return url.startsWith("http")
                              ? url
                              : `${getBackendUrl()}${url}`;
                          })()}
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          <Upload className="h-4 w-4" />
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium text-gray-900">
                      {item.original_filename}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                      {item.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatFileSize(item.size)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(item)}
                      className="text-red-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedMedia && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h3 className="text-lg font-semibold">Media Details</h3>
              <button
                onClick={() => setSelectedMedia(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6 space-y-4 overflow-y-auto">
              {selectedMedia.type === "image" ? (
                <img
                  src={(() => {
                    const url =
                      selectedMedia.url || `/storage/${selectedMedia.path}`;
                    return url.startsWith("http")
                      ? url
                      : `${getBackendUrl()}${url}`;
                  })()}
                  alt={
                    selectedMedia.alt_text || selectedMedia.original_filename
                  }
                  className="w-full rounded-lg max-h-[60vh] object-contain"
                />
              ) : (
                <video
                  controls
                  className="w-full rounded-lg max-h-[60vh] object-contain"
                >
                  <source
                    src={(() => {
                      const url =
                        selectedMedia.url || `/storage/${selectedMedia.path}`;
                      return url.startsWith("http")
                        ? url
                        : `${getBackendUrl()}${url}`;
                    })()}
                    type={selectedMedia.mime_type}
                  />
                </video>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-gray-500 font-medium">Name</div>
                  <div className="text-gray-900">
                    {selectedMedia.original_filename}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Type</div>
                  <div className="text-gray-900">{selectedMedia.type}</div>
                </div>
                <div>
                  <div className="text-gray-500 font-medium">Size</div>
                  <div className="text-gray-900">
                    {formatFileSize(selectedMedia.size)}
                  </div>
                </div>
                {selectedMedia.width && selectedMedia.height && (
                  <div>
                    <div className="text-gray-500 font-medium">Dimensions</div>
                    <div className="text-gray-900">
                      {selectedMedia.width} × {selectedMedia.height}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="text-gray-500 font-medium mb-2">URL</div>
                <div className="flex items-center gap-2">
                  <Input
                    value={(() => {
                      const url =
                        selectedMedia.url || `/storage/${selectedMedia.path}`;
                      return url.startsWith("http")
                        ? url
                        : `${getBackendUrl()}${url}`;
                    })()}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button
                    variant="secondary"
                    onClick={() => copyUrl(selectedMedia)}
                  >
                    Copy
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
